import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  HardDrive,
  Database,
  Server,
  BarChart3,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Settings,
  Plus,
  Download,
  Upload,
  Search,
  Filter,
  AlertTriangle,
  CheckCircle,
  Clock,
  Zap,
  Activity
} from 'lucide-react';
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { formatNumber, formatRelativeTime } from '../utils/format'
import { 
  ResponsiveContainer, 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

interface StorageNode {
  id: string;
  name: string;
  status: 'online' | 'offline' | 'degraded' | 'maintenance';
  totalCapacity: number; // GB
  usedCapacity: number; // GB
  availableCapacity: number; // GB
  iops: number;
  throughput: number; // MB/s
  latency: number; // ms
  temperature: number; // celsius
  replicationFactor: number;
  lastUpdate: Date;
  dataTypes: string[];
  health: number; // 0-100
}

interface StoragePool {
  id: string;
  name: string;
  type: 'distributed' | 'replicated' | 'erasure-coded';
  nodes: string[];
  totalCapacity: number;
  usedCapacity: number;
  compressionRatio: number;
  deduplicationRatio: number;
  redundancyLevel: string;
  status: 'healthy' | 'degraded' | 'critical';
}

interface ReplicationJob {
  id: string;
  sourceNode: string;
  targetNode: string;
  dataSize: number; // GB
  progress: number; // 0-100
  status: 'running' | 'completed' | 'failed' | 'queued';
  startTime: Date;
  estimatedCompletion?: Date;
  priority: 'high' | 'medium' | 'low';
}

// Mock data
const generateStorageNodes = (): StorageNode[] => [
  {
    id: 'storage-node-1',
    name: 'Storage Alpha',
    status: 'online',
    totalCapacity: 2048,
    usedCapacity: 1536,
    availableCapacity: 512,
    iops: 12500,
    throughput: 850,
    latency: 2.3,
    temperature: 42,
    replicationFactor: 3,
    lastUpdate: new Date(Date.now() - 30 * 1000),
    dataTypes: ['blocks', 'objects', 'files'],
    health: 98
  },
  {
    id: 'storage-node-2',
    name: 'Storage Beta',
    status: 'online',
    totalCapacity: 4096,
    usedCapacity: 2458,
    availableCapacity: 1638,
    iops: 15200,
    throughput: 1200,
    latency: 1.8,
    temperature: 38,
    replicationFactor: 3,
    lastUpdate: new Date(Date.now() - 45 * 1000),
    dataTypes: ['blocks', 'objects'],
    health: 95
  },
  {
    id: 'storage-node-3',
    name: 'Storage Gamma',
    status: 'degraded',
    totalCapacity: 1024,
    usedCapacity: 923,
    availableCapacity: 101,
    iops: 8500,
    throughput: 450,
    latency: 5.2,
    temperature: 58,
    replicationFactor: 2,
    lastUpdate: new Date(Date.now() - 5 * 60 * 1000),
    dataTypes: ['files'],
    health: 72
  },
  {
    id: 'storage-node-4',
    name: 'Storage Delta',
    status: 'maintenance',
    totalCapacity: 8192,
    usedCapacity: 3276,
    availableCapacity: 4916,
    iops: 0,
    throughput: 0,
    latency: 0,
    temperature: 25,
    replicationFactor: 3,
    lastUpdate: new Date(Date.now() - 2 * 60 * 60 * 1000),
    dataTypes: ['blocks', 'objects', 'files'],
    health: 85
  }
];

const mockStoragePools: StoragePool[] = [
  {
    id: 'pool-1',
    name: 'Primary Pool',
    type: 'distributed',
    nodes: ['storage-node-1', 'storage-node-2', 'storage-node-4'],
    totalCapacity: 12288,
    usedCapacity: 6144,
    compressionRatio: 2.1,
    deduplicationRatio: 1.4,
    redundancyLevel: '3-way mirror',
    status: 'healthy'
  },
  {
    id: 'pool-2',
    name: 'Archive Pool',
    type: 'erasure-coded',
    nodes: ['storage-node-2', 'storage-node-3', 'storage-node-4'],
    totalCapacity: 8192,
    usedCapacity: 3276,
    compressionRatio: 3.2,
    deduplicationRatio: 2.1,
    redundancyLevel: '6+2 erasure',
    status: 'healthy'
  },
  {
    id: 'pool-3',
    name: 'Hot Data Pool',
    type: 'replicated',
    nodes: ['storage-node-1', 'storage-node-2'],
    totalCapacity: 4096,
    usedCapacity: 2048,
    compressionRatio: 1.2,
    deduplicationRatio: 1.1,
    redundancyLevel: '2-way mirror',
    status: 'degraded'
  }
];

const mockReplicationJobs: ReplicationJob[] = [
  {
    id: 'repl-1',
    sourceNode: 'storage-node-1',
    targetNode: 'storage-node-2',
    dataSize: 256,
    progress: 78,
    status: 'running',
    startTime: new Date(Date.now() - 2 * 60 * 60 * 1000),
    estimatedCompletion: new Date(Date.now() + 30 * 60 * 1000),
    priority: 'high'
  },
  {
    id: 'repl-2',
    sourceNode: 'storage-node-2',
    targetNode: 'storage-node-4',
    dataSize: 128,
    progress: 100,
    status: 'completed',
    startTime: new Date(Date.now() - 4 * 60 * 60 * 1000),
    priority: 'medium'
  },
  {
    id: 'repl-3',
    sourceNode: 'storage-node-3',
    targetNode: 'storage-node-1',
    dataSize: 64,
    progress: 0,
    status: 'queued',
    startTime: new Date(),
    priority: 'low'
  }
];

export const StoragePage: React.FC = () => {
  const [storageNodes] = useState<StorageNode[]>(generateStorageNodes());
  const [storagePools] = useState<StoragePool[]>(mockStoragePools);
  const [replicationJobs] = useState<ReplicationJob[]>(mockReplicationJobs);
  const [selectedTab, setSelectedTab] = useState<'overview' | 'nodes' | 'pools' | 'replication'>('overview');
  const [searchTerm, setSearchTerm] = useState('');

  const totalStats = useMemo(() => {
    const onlineNodes = storageNodes.filter(node => node.status === 'online');
    const totalCapacity = storageNodes.reduce((sum, node) => sum + node.totalCapacity, 0);
    const usedCapacity = storageNodes.reduce((sum, node) => sum + node.usedCapacity, 0);
    const totalIOPS = onlineNodes.reduce((sum, node) => sum + node.iops, 0);
    const avgLatency = onlineNodes.length > 0 
      ? onlineNodes.reduce((sum, node) => sum + node.latency, 0) / onlineNodes.length 
      : 0;

    return {
      totalCapacity,
      usedCapacity,
      availableCapacity: totalCapacity - usedCapacity,
      usagePercentage: (usedCapacity / totalCapacity) * 100,
      totalIOPS,
      avgLatency: Math.round(avgLatency * 10) / 10,
      onlineNodes: onlineNodes.length,
      totalNodes: storageNodes.length
    };
  }, [storageNodes]);

  // Generate mock performance data
  const performanceData = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 24 }, (_, i) => ({
      time: new Date(now.getTime() - (23 - i) * 60 * 60 * 1000).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      capacity: Math.min(95, 60 + Math.random() * 20 + i * 0.5),
      iops: Math.floor(8000 + Math.random() * 4000 + Math.sin(i * 0.5) * 2000),
      throughput: Math.floor(400 + Math.random() * 200 + Math.sin(i * 0.3) * 100),
      latency: Math.max(0.5, 2 + Math.random() * 2 + Math.sin(i * 0.7) * 1)
    }));
  }, []);

  const storageDistribution = [
    { name: 'Used', value: totalStats.usedCapacity, color: '#3b82f6' },
    { name: 'Available', value: totalStats.availableCapacity, color: '#22c55e' }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-500';
      case 'degraded': return 'text-yellow-500';
      case 'offline': return 'text-red-500';
      case 'maintenance': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'degraded': return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'offline': return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'maintenance': return <Settings className="w-4 h-4 text-blue-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-console-text">Storage Management</h1>
          <p className="text-console-muted mt-1">Monitor and manage distributed storage infrastructure</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="secondary">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="secondary">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button>
            <Plus className="w-4 h-4 mr-2" />
            Add Node
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Total Capacity</p>
                <p className="text-2xl font-bold text-console-text">{formatNumber(totalStats.totalCapacity)} GB</p>
                <p className="text-xs text-console-muted">{Math.round(totalStats.usagePercentage)}% used</p>
              </div>
              <HardDrive className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Available Space</p>
                <p className="text-2xl font-bold text-green-500">{formatNumber(totalStats.availableCapacity)} GB</p>
                <p className="text-xs text-console-muted">{Math.round(100 - totalStats.usagePercentage)}% free</p>
              </div>
              <Database className="w-6 h-6 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Total IOPS</p>
                <p className="text-2xl font-bold text-console-text">{formatNumber(totalStats.totalIOPS)}</p>
                <p className="text-xs text-console-muted">operations/sec</p>
              </div>
              <Activity className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-console-muted">Avg Latency</p>
                <p className="text-2xl font-bold text-console-text">{totalStats.avgLatency}ms</p>
                <p className="text-xs text-console-muted">{totalStats.onlineNodes}/{totalStats.totalNodes} nodes online</p>
              </div>
              <Zap className="w-6 h-6 text-console-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="border-b border-console-border">
        <nav className="flex space-x-8">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'nodes', label: 'Storage Nodes', count: storageNodes.length },
            { id: 'pools', label: 'Storage Pools', count: storagePools.length },
            { id: 'replication', label: 'Replication', count: replicationJobs.filter(j => j.status === 'running').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                selectedTab === tab.id
                  ? 'border-console-accent text-console-accent'
                  : 'border-transparent text-console-muted hover:text-console-text hover:border-console-border'
              }`}
            >
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-2 bg-console-surface px-2 py-1 rounded-full text-xs">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="space-y-6">
        {selectedTab === 'overview' && (
          <>
            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Performance Chart */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <h3 className="text-lg font-semibold text-console-text">Storage Performance</h3>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={performanceData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#2d3748" />
                          <XAxis dataKey="time" stroke="#718096" fontSize={12} />
                          <YAxis stroke="#718096" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ 
                              backgroundColor: 'rgba(31, 41, 55, 0.98)', 
                              border: '1px solid rgba(75, 85, 99, 0.8)',
                              borderRadius: '8px'
                            }}
                          />
                          <Area type="monotone" dataKey="iops" stackId="1" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} />
                          <Area type="monotone" dataKey="throughput" stackId="2" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Storage Distribution */}
              <Card>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-console-text">Storage Distribution</h3>
                </CardHeader>
                <CardContent>
                  <div className="h-64 flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={storageDistribution}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {storageDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number | undefined) => [`${formatNumber(value ?? 0)} GB`, 'Capacity']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="mt-4 space-y-2">
                    {storageDistribution.map((item) => (
                      <div key={item.name} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: item.color }}
                           />
                          <span className="text-console-muted">{item.name}</span>
                        </div>
                        <span className="text-console-text font-medium">{formatNumber(item.value)} GB</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <h3 className="text-lg font-semibold text-console-text">Recent Activity</h3>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {replicationJobs.slice(0, 3).map((job) => (
                    <div key={job.id} className="flex items-center justify-between p-3 bg-console-surface rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          job.status === 'running' ? 'bg-blue-500 animate-pulse' :
                          job.status === 'completed' ? 'bg-green-500' :
                          job.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                        }`} />
                        <div>
                          <p className="text-sm font-medium text-console-text">
                            Replication: {job.sourceNode} → {job.targetNode}
                          </p>
                          <p className="text-xs text-console-muted">
                            {formatNumber(job.dataSize)} GB • {job.status} • {formatRelativeTime(job.startTime)}
                          </p>
                        </div>
                      </div>
                      {job.status === 'running' && (
                        <div className="text-right">
                          <p className="text-sm font-medium text-console-text">{job.progress}%</p>
                          <div className="w-20 h-2 bg-console-border rounded-full">
                            <div 
                              className="h-2 bg-console-accent rounded-full transition-all"
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {selectedTab === 'nodes' && (
          <div className="space-y-4">
            <div className="flex items-center gap-4 p-4 bg-console-surface rounded-lg">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-console-muted" />
                  <input
                    type="text"
                    placeholder="Search storage nodes..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-console-bg border border-console-border rounded-lg text-console-text placeholder-console-muted focus:ring-2 focus:ring-console-accent focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {storageNodes.map((node, index) => (
              <motion.div
                key={node.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4 flex-1">
                        <div className="flex flex-col items-center gap-2">
                          {getStatusIcon(node.status)}
                          <Server className="w-6 h-6 text-console-accent" />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-console-text">{node.name}</h3>
                            <Badge variant={node.status === 'online' ? 'success' : node.status === 'degraded' ? 'warning' : 'default'}>
                              {node.status}
                            </Badge>
                            <Badge variant="default">Health: {node.health}%</Badge>
                          </div>
                          
                          <div className="metrics-grid text-sm">
                            <div>
                              <p className="text-console-muted">Capacity</p>
                              <p className="text-console-text font-medium">
                                {formatNumber(node.usedCapacity)} / {formatNumber(node.totalCapacity)} GB
                              </p>
                              <div className="w-full h-2 bg-console-border rounded-full mt-1">
                                <div 
                                  className="h-2 bg-console-accent rounded-full"
                                  style={{ width: `${(node.usedCapacity / node.totalCapacity) * 100}%` }}
                                />
                              </div>
                            </div>
                            
                            <div>
                              <p className="text-console-muted">IOPS</p>
                              <p className="text-console-text font-medium">{formatNumber(node.iops)}</p>
                            </div>
                            
                            <div>
                              <p className="text-console-muted">Throughput</p>
                              <p className="text-console-text font-medium">{formatNumber(node.throughput)} MB/s</p>
                            </div>
                            
                            <div>
                              <p className="text-console-muted">Latency</p>
                              <p className="text-console-text font-medium">{node.latency}ms</p>
                            </div>
                          </div>

                          <div className="flex items-center gap-4 mt-3 text-xs text-console-muted">
                            <span>Temp: {node.temperature}°C</span>
                            <span>Replication: {node.replicationFactor}x</span>
                            <span>Types: {node.dataTypes.join(', ')}</span>
                            <span>Updated: {formatRelativeTime(node.lastUpdate)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="secondary" size="sm">
                          Configure
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {selectedTab === 'pools' && (
          <div className="space-y-4">
            {storagePools.map((pool, index) => (
              <motion.div
                key={pool.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold text-console-text">{pool.name}</h3>
                          <Badge variant={pool.status === 'healthy' ? 'success' : pool.status === 'degraded' ? 'warning' : 'danger'}>
                            {pool.status}
                          </Badge>
                          <Badge variant="default">{pool.type}</Badge>
                        </div>
                        
                        <div className="metrics-grid text-sm mb-4">
                          <div>
                            <p className="text-console-muted">Capacity</p>
                            <p className="text-console-text font-medium">
                              {formatNumber(pool.usedCapacity)} / {formatNumber(pool.totalCapacity)} GB
                            </p>
                            <div className="w-full h-2 bg-console-border rounded-full mt-1">
                              <div 
                                className="h-2 bg-console-accent rounded-full"
                                style={{ width: `${(pool.usedCapacity / pool.totalCapacity) * 100}%` }}
                              />
                            </div>
                          </div>
                          
                          <div>
                            <p className="text-console-muted">Compression</p>
                            <p className="text-console-text font-medium">{pool.compressionRatio}:1</p>
                          </div>
                          
                          <div>
                            <p className="text-console-muted">Deduplication</p>
                            <p className="text-console-text font-medium">{pool.deduplicationRatio}:1</p>
                          </div>
                          
                          <div>
                            <p className="text-console-muted">Redundancy</p>
                            <p className="text-console-text font-medium">{pool.redundancyLevel}</p>
                          </div>
                        </div>

                        <div className="text-xs text-console-muted">
                          Nodes: {pool.nodes.join(', ')}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button variant="secondary" size="sm">
                          Manage
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {selectedTab === 'replication' && (
          <div className="space-y-4">
            {replicationJobs.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-4 h-4 rounded-full ${
                          job.status === 'running' ? 'bg-blue-500 animate-pulse' :
                          job.status === 'completed' ? 'bg-green-500' :
                          job.status === 'failed' ? 'bg-red-500' : 'bg-yellow-500'
                        }`} />
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-console-text">
                              {job.sourceNode} → {job.targetNode}
                            </h3>
                            <Badge variant={
                              job.status === 'completed' ? 'success' :
                              job.status === 'failed' ? 'danger' :
                              job.status === 'running' ? 'warning' : 'default'
                            }>
                              {job.status}
                            </Badge>
                            <Badge variant={
                              job.priority === 'high' ? 'danger' :
                              job.priority === 'medium' ? 'warning' : 'default'
                            }>
                              {job.priority} priority
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-6 text-sm text-console-muted">
                            <span>Size: {formatNumber(job.dataSize)} GB</span>
                            <span>Started: {formatRelativeTime(job.startTime)}</span>
                            {job.estimatedCompletion && (
                              <span>ETA: {formatRelativeTime(job.estimatedCompletion)}</span>
                            )}
                          </div>

                          {job.status === 'running' && (
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-console-muted">Progress</span>
                                <span className="text-console-text font-medium">{job.progress}%</span>
                              </div>
                              <div className="w-full h-2 bg-console-border rounded-full">
                                <div 
                                  className="h-2 bg-console-accent rounded-full transition-all"
                                  style={{ width: `${job.progress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {job.status === 'running' && (
                          <Button variant="secondary" size="sm">
                            Pause
                          </Button>
                        )}
                        {job.status === 'failed' && (
                          <Button variant="warning" size="sm">
                            Retry
                          </Button>
                        )}
                        <Button variant="ghost" size="sm">
                          <Settings className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};